import React from 'react';
import { Paperclip, Flag, MoreVertical } from 'lucide-react';
import EmailCategoryBadge from './EmailCategoryBadge';
import EmailDropdown from './EmailDropdown';

/**
 * Decode HTML entities (&#39; -> ', &amp; -> &, etc.)
 */
const decodeHtmlEntities = (str) => {
  if (!str) return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

/**
 * EmailListItem - Compact email item for better density (8 emails visible without scrolling)
 */
const EmailListItemCard = ({
  email,
  isFlagged,
  isSelected,
  isDropdownOpen,
  onSelect,
  onToggleFlag,
  onToggleDropdown,
  onReply,
  onAddToMeeting,
  onNewMeeting,
  onAddContact,
  onNewContact,
  onDelete,
  onCloseDropdown
}) => (
  <div className="relative">
    <div 
      className={`group relative rounded-lg px-3 py-2 transition-all cursor-pointer border ${
        isSelected 
          ? 'bg-gray-800 border-emerald-500/50' 
          : 'bg-black border-gray-400 hover:bg-gray-900'
      }`}
      onClick={() => onSelect?.(email)}
      data-testid={`email-card-${email.id}`}
    >
      {/* Single row layout for compact view */}
      <div className="flex items-center gap-3">
        {/* Sender info - fixed width */}
        <div className="min-w-0 w-40 flex-shrink-0">
          <h4 className="text-sm font-semibold text-white truncate">{decodeHtmlEntities(email.from)}</h4>
          <p className="text-xs text-gray-500 truncate">{decodeHtmlEntities(email.email) || 'no-reply@email.com'}</p>
        </div>
        
        {/* Subject and Preview - flex grow */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white truncate">{decodeHtmlEntities(email.subject)}</h3>
            {email.hasAttachments && (
              <Paperclip size={12} className="text-gray-400 flex-shrink-0" title="Has attachments" />
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{decodeHtmlEntities(email.preview)}</p>
        </div>
        
        {/* Right side: Badge, Time, Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <EmailCategoryBadge category={email.category} size="sm" />
          <span className="text-xs text-gray-400 w-16 text-right">{email.time}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFlag?.(email.id);
            }}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
            title={isFlagged ? "Unflag email" : "Flag email"}
            data-testid={`email-flag-${email.id}`}
          >
            <Flag 
              size={14} 
              className={`${isFlagged ? 'text-yellow-500 fill-yellow-500' : 'text-gray-500'}`}
            />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleDropdown?.();
            }}
            className="p-1 hover:bg-gray-700 rounded-full transition-colors opacity-0 group-hover:opacity-100"
            data-testid={`email-menu-${email.id}`}
          >
            <MoreVertical size={14} className="text-gray-400" />
          </button>
        </div>
      </div>
    </div>
    
    {/* Dropdown Menu */}
    <EmailDropdown
      isOpen={isDropdownOpen}
      onClose={onCloseDropdown}
      email={email}
      onReply={onReply}
      onAddToMeeting={onAddToMeeting}
      onNewMeeting={onNewMeeting}
      onAddContact={onAddContact}
      onNewContact={onNewContact}
      onDelete={onDelete}
      position="right-4 top-12"
    />
  </div>
);

/**
 * EmailList - Compact list of emails optimized to show 8+ rows without scrolling
 */
const EmailList = ({
  emails = [],
  flaggedEmails = {},
  openDropdownId,
  selectedEmailId,
  onSelectEmail,
  onToggleFlag,
  onToggleDropdown,
  onCloseDropdown,
  onReplyEmail,
  onAddToMeeting,
  onNewMeeting,
  onDeleteEmail,
  listHeight = 'calc(100vh - 320px)',
  className = ''
}) => {
  return (
    <div 
      className={`overflow-y-auto px-3 py-2 space-y-1.5 ${className}`} 
      style={{ height: listHeight, maxHeight: listHeight }}
      data-testid="email-list-container"
    >
      {emails.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
          No emails found
        </div>
      ) : (
        emails.map((email) => (
          <EmailListItemCard
            key={email.id}
            email={email}
            isFlagged={flaggedEmails[email.id] || email.isFlagged}
            isSelected={selectedEmailId === email.id}
            isDropdownOpen={openDropdownId === `list-${email.id}`}
            onSelect={onSelectEmail}
            onToggleFlag={onToggleFlag}
            onToggleDropdown={() => onToggleDropdown?.(`list-${email.id}`)}
            onReply={() => onReplyEmail?.(email)}
            onAddToMeeting={() => onAddToMeeting?.({ name: email.from, phone: email.email, id: email.id })}
            onNewMeeting={() => onNewMeeting?.({ name: email.from, phone: email.email, id: email.id })}
            onAddContact={() => onCloseDropdown?.()}
            onNewContact={() => onCloseDropdown?.()}
            onDelete={() => {
              onDeleteEmail?.(email.id);
              onCloseDropdown?.();
            }}
            onCloseDropdown={onCloseDropdown}
          />
        ))
      )}
    </div>
  );
};

export default EmailList;
